import type { ShortStatusType, StatusHistory, Video, VideoStatusType, VideoWithRelations } from "@repo/types";
import { db } from "../client";

/**
 * Status History Helper Functions
 * These functions manage the statusHistory array for video status tracking
 */

/**
 * Add a new status entry to the status history
 * Ends the current status (if any) and starts a new one
 * @param currentHistory - Current status history array
 * @param newStatus - New status to add
 * @returns Updated status history array
 */
function transitionVideoStatus(
	currentHistory: StatusHistory[] | null | undefined,
	newStatus: VideoStatusType,
): StatusHistory[] {
	const history: StatusHistory[] = currentHistory || [];
	const now = new Date().toISOString();

	// End the current status if there is one without an endTime
	const updatedHistory = history.map((entry) => {
		if (!entry.endTime) {
			return {
				...entry,
				endTime: now,
			};
		}
		return entry;
	});

	// Add the new status entry
	updatedHistory.push({
		status: newStatus as string, // VideoStatusType is compatible with string
		startTime: now,
		endTime: null, // Current status has no end time
	});

	return updatedHistory;
}

/**
 * Initialize status history with the first status
 * @param initialStatus - Initial status value
 * @returns Status history array with initial entry
 */
function initializeStatusHistory(initialStatus: VideoStatusType): StatusHistory[] {
	return [
		{
			status: initialStatus as string, // VideoStatusType is compatible with string
			startTime: new Date().toISOString(),
			endTime: null,
		},
	];
}

/**
 * Helper function to enrich update data with statusHistory if status is being updated
 * This ensures status tracking happens automatically whenever status changes
 * @param videoId - Video ID to get current statusHistory
 * @param updateData - Update data that may contain status
 * @returns Enriched update data with statusHistory if status is present
 */
async function enrichUpdateDataWithStatusHistory(
	videoId: string,
	updateData: any,
): Promise<any> {
	// If status is being updated, track it in statusHistory
	if (updateData.status) {
		const currentVideo = await db.video.findUnique({
			where: { id: videoId },
			select: { statusHistory: true },
		});

		// Update status history
		const updatedStatusHistory = currentVideo?.statusHistory
			? transitionVideoStatus(currentVideo.statusHistory as any, updateData.status)
			: initializeStatusHistory(updateData.status);

		return {
			...updateData,
			statusHistory: updatedStatusHistory,
		};
	}

	return updateData;
}


// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export class DBVideoService {
	public static async getVideoById(
		videoId: string,
		includeJob = false,
	): Promise<VideoWithRelations | null> {
		const video = await db.video.findFirst({
			where: {
				id: videoId,
			},
			include: {
				job: includeJob,
			},
		});
		return video as VideoWithRelations | null;
	}

	public static async changeVideoStatusAndSave(
		videoId: string,
		status: VideoStatusType,
	): Promise<Video> {
		// Get current video to access statusHistory
		const currentVideo = await db.video.findUnique({
			where: { id: videoId },
			select: { statusHistory: true },
		});

		// Update status history
		const updatedStatusHistory = currentVideo?.statusHistory
			? transitionVideoStatus(currentVideo.statusHistory as any, status)
			: initializeStatusHistory(status);

		const result = await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				status: status,
				statusHistory: updatedStatusHistory as any,
			},
		});
		return result;
	}

	public static async setVideoShortASD(
		videoId: string,
		shortId: number,
		asd: any, // AsdRoot,
	): Promise<Video> {
		console.log("setVideoShortASD updating shortASD", videoId, shortId);
		const result = await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				shorts: {
					updateMany: {
						where: {
							id: shortId,
						},
						data: {
							asd,
						},
					},
				},
			},
		});
		return result;
	}

	public static async setVideoShortCrop(
		videoId: string,
		shortId: number,
		cropFileUrl: string,
		cropFilekey: string,
		appFileId: string,
	): Promise<Video> {
		console.log("setVideoShortASD updating autoCrop", videoId, shortId);
		const result = await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				shorts: {
					updateMany: {
						where: {
							id: shortId,
						},
						data: {
							cropFileUrl,
							cropFilekey,
							appFileId
						},
					},
				},
			},
		});
		return result;
	}

	public static async setVideoFilesFPS(
		videoId: string,
		targetFPS: number,
		previewFPS: number,
	): Promise<Video> {
		console.log("setVideoFilesFPS updating file FPS", videoId);
		const video = await DBVideoService.getVideoById(videoId);
		const result = await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				files: {
					...(video?.files || {}),
					targetFPS,
					previewFPS,
				},
			},
		});
		return result;
	}

	public static async setVideoFiles(
		videoId: string,
		field: "target" | "preview",
		value: string,
		isInS3 = true,
	): Promise<Video> {
		console.log("setVideoFiles updating files", videoId, field, value);
		const video = await DBVideoService.getVideoById(videoId);
		const files = {
			...(video?.files || {}),
			isInS3,
		} as any;
		files[field] = value;
		return await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				files,
			},
		});
	}

	public static async updateVideo(
		videoId: string,
		updateData: Partial<VideoWithRelations>,
	): Promise<Video> {
		const data: any = {
			...updateData,
		};
		data.id = undefined;
		data.user = undefined;

		// Enrich with statusHistory if status is being updated
		const enrichedData = await enrichUpdateDataWithStatusHistory(videoId, data);

		const updatedUser = await db.video.update({
			where: {
				id: videoId,
			},
			data: enrichedData,
		});
		return updatedUser;
	}

	public static async getExistingPredictionBySourceId(
		sourceId: string,
		isRTL = false,
	) {
		const doc = await db.video.findFirst({
			where: {
				sourceId,
				transcription: {
					isNot: { prediction: { isEmpty: true } },
				},
				// options: {
				//   isRTL
				// }
			},
			// $where: "this.transcription.prediction.length > 0"
		});
		return doc;
	}

	/**
	 * Update short status in database
	 */
	public static async updateShortStatus(
		videoId: string,
		shortId: number,
		status: ShortStatusType,
		requestId = "",
	): Promise<void> {
		await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				shorts: {
					updateMany: {
						where: {
							id: shortId,
						},
						data: {
							status,
						},
					},
				},
			},
		});
		console.log(
			`${requestId} [shortVideoTrim] - Updated short ${shortId} status to ${status}`,
		);
	}

	/**
	 * Update B-roll items for a specific short
	 */
	public static async updateShortBRolls(
		videoId: string,
		shortId: number,
		bRolls: any[], // BRollItem[]
		requestId = "",
	): Promise<Video> {
		console.log(
			`${requestId} [updateShortBRolls] - Updating B-rolls for video ${videoId}, short ${shortId}`,
		);

		// For MongoDB nested array updates, we need to update the entire shorts array
		// First, get the current video to access the shorts array
		const video = await DBVideoService.getVideoById(videoId);
		if (!video) {
			throw new Error(`Video not found: ${videoId}`);
		}

		// Find and update the specific short's bRolls
		const updatedShorts = (video.shorts || []).map((short) => {
			if (short.id === shortId) {
				return {
					...short,
					bRolls,
				};
			}
			return short;
		});

		// Update the video with the modified shorts array
		const result = await db.video.update({
			where: {
				id: videoId,
			},
			data: {
				shorts: updatedShorts,
			},
		});

		console.log(
			`${requestId} [updateShortBRolls] - Successfully updated B-rolls for short ${shortId}`,
		);

		return result;
	}

	/**
	 * Get a specific short with B-rolls from a video
	 */
	public static async getShortWithBRolls(
		videoId: string,
		shortId: number,
	): Promise<any | null> {
		const video = await DBVideoService.getVideoById(videoId);
		if (!video || !video.shorts) {
			return null;
		}

		const short = video.shorts.find((s) => s.id === shortId);
		return short || null;
	}

	/**
	 * Public method to update statusHistory for a video
	 * Used when status needs to be tracked outside of updateVideo
	 * @param videoId - Video ID
	 * @param newStatus - New status to track
	 * @returns Updated statusHistory array
	 */
	public static async updateVideoStatusHistory(
		videoId: string,
		newStatus: VideoStatusType,
	): Promise<StatusHistory[]> {
		const currentVideo = await db.video.findUnique({
			where: { id: videoId },
			select: { statusHistory: true },
		});

		const updatedStatusHistory = currentVideo?.statusHistory
			? transitionVideoStatus(currentVideo.statusHistory as any, newStatus)
			: initializeStatusHistory(newStatus);

		return updatedStatusHistory;
	}

}
